import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import ChatBubble from './Chat/ChatBubble';
import EmailVerifyBanner from './EmailVerifyBanner';
import ContactWidget from './ContactWidget';
import InstallPWAButton from './InstallPWAButton';
import CoopInvitePopup from './CoopInvitePopup';
import ReviewInvitePopup from './ReviewInvitePopup';
import PendingWorkBanner from './PendingWorkBanner';
import { usePendingWork } from '../contexts/PendingWorkContext';
import api from '../services/api';
import socketService from '../services/socket';
import './Layout.css';

const formatBadge = (n) => (n > 99 ? '99+' : n);

function Layout() {
    const { user, logout, isAdmin, canAccessAdmin, hasPermission, hasAnyPermission } = useAuth();
    const { appName, appCredit } = useBranding();
    const location = useLocation();
    const navigate = useNavigate();
    const { total: pendingTotal, countFor: pendingCountFor } = usePendingWork();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [adminUnreadCount, setAdminUnreadCount] = useState(0);
    const [userNotificationCount, setUserNotificationCount] = useState(0);
    const [onlineCount, setOnlineCount] = useState(0);

    // เมนู "คำร้อง" แยกโชว์เฉพาะเจ้าหน้าที่การเงิน (ไม่ใช่ admin) — ถ้ามี ให้ badge คำร้องเอกสารไปอยู่ที่นั่น
    // และไม่นับซ้ำในเมนู "จัดการระบบ"
    const showRequestsNav = canAccessAdmin() && !isAdmin() && hasAnyPermission(['request.view_all', 'request.process', 'request.approve']);
    const requestsBadge = pendingCountFor('/admin/requests');
    const adminBadge = Math.max(0, pendingTotal - (showRequestsNav ? requestsBadge : 0));

    // ตรวจสอบว่าเป็นเจ้าหน้าที่การเงินเท่านั้น (ไม่ใช่ admin เต็มตัว)
    const isFinanceStaffOnly = useCallback(() => {
        if (isAdmin()) return false; // Admin เต็มตัวไม่ใช่
        // เป็นเจ้าหน้าที่การเงินถ้ามี permission เหล่านี้
        return hasAnyPermission(['request.view_all', 'request.process', 'request.approve']);
    }, [isAdmin, hasAnyPermission]);

    // Fetch admin unread count
    const fetchAdminUnreadCount = useCallback(async () => {
        if (canAccessAdmin()) {
            try {
                const response = await api.get('/chat/admin/unread-count');
                setAdminUnreadCount(response.data.total_unread || response.data.count || 0);
            } catch (err) {
                // Ignore if no permission
            }
        }
    }, [canAccessAdmin]);

    // Fetch user notification count (คำร้องที่อนุมัติ/ปฏิเสธ ที่ยังไม่ได้อ่าน)
    const fetchUserNotificationCount = useCallback(async () => {
        if (!isFinanceStaffOnly()) {
            try {
                const response = await api.get('/request/my-notifications-count');
                setUserNotificationCount(response.data.count || 0);
            } catch (err) {
                // Ignore error
            }
        }
    }, [isFinanceStaffOnly]);

    useEffect(() => {
        fetchAdminUnreadCount();
        fetchUserNotificationCount();
        // Socket.io อัปเดต count แบบ real-time อยู่แล้ว (ดู useEffect ด้านล่าง)
        // poll นี้เป็นแค่ fallback กันกรณี socket หลุด — ใช้ 2 นาทีก็พอ (เดิม 30 วิ ยิงถี่เกินจำเป็น)
        const interval = setInterval(() => {
            fetchAdminUnreadCount();
            fetchUserNotificationCount();
        }, 120000);
        return () => clearInterval(interval);
    }, [fetchAdminUnreadCount, fetchUserNotificationCount]);

    // Socket for notifications
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            socketService.connect(token);

            if (canAccessAdmin()) {
                socketService.on('new_message', () => {
                    fetchAdminUnreadCount();
                });
                socketService.on('new_conversation', () => {
                    fetchAdminUnreadCount();
                });
            }

            // Listen for request updates for regular users
            if (!isFinanceStaffOnly()) {
                socketService.on('request_updated', () => {
                    fetchUserNotificationCount();
                });
            }
        }

        return () => {
            socketService.off('new_message');
            socketService.off('new_conversation');
            socketService.off('request_updated');
        };
    }, [canAccessAdmin, fetchAdminUnreadCount, fetchUserNotificationCount, isFinanceStaffOnly]);

    // จำนวนผู้ใช้งานออนไลน์ (นับ session จริงจาก socket — ทุก role)
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        socketService.connect(token);

        const handleOnlineCount = (data) => setOnlineCount(data?.count || 0);
        const requestCount = () => socketService.emit('get_online_count');

        socketService.on('online_count', handleOnlineCount);
        // ขอค่าล่าสุดทุกครั้งที่ (re)connect และทันทีถ้า socket เชื่อมต่ออยู่แล้ว
        socketService.on('connect', requestCount);
        requestCount();

        return () => {
            socketService.off('online_count', handleOnlineCount);
            socketService.off('connect', requestCount);
        };
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className="layout">
            {/* Header */}
            <header className="header">
                <div className="header-container">
                    <Link to="/" className="logo">
                        <img
                            src={`${process.env.PUBLIC_URL}/icon-192.png`}
                            alt={appName}
                            className="logo-icon"
                        />
                        <div className="logo-text-group">
                            <span className="logo-text">{appName}</span>
                            <span className="logo-credit">{appCredit}</span>
                        </div>
                    </Link>

                    <nav className={`nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
                        {/* ซ่อนเมนูหน้าหลักและยื่นคำร้องสำหรับเจ้าหน้าที่การเงินและ admin */}
                        {!isFinanceStaffOnly() && !isAdmin() && (
                            <>
                                <Link
                                    to="/"
                                    className={`nav-link ${isActive('/') && !isActive('/admin') && !isActive('/print') && !isActive('/yearly') && !isActive('/profile') ? 'active' : ''}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    หน้าหลัก
                                </Link>
                                <Link
                                    to="/request"
                                    className={`nav-link nav-link-request ${isActive('/request') ? 'active' : ''}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    ยื่นคำร้อง
                                    {userNotificationCount > 0 && (
                                        <span className="nav-badge">{formatBadge(userNotificationCount)}</span>
                                    )}
                                </Link>
                            </>
                        )}
                        {canAccessAdmin() && (
                            <>
                                {/* ซ่อนเมนูคำร้องและแชทสำหรับ admin (แสดงเฉพาะจัดการระบบ) */}
                                {showRequestsNav && (
                                    <Link
                                        to="/admin/requests"
                                        className={`nav-link nav-link-work ${isActive('/admin/requests') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                        title={requestsBadge > 0 ? `คำร้องเอกสารรอดำเนินการ ${requestsBadge} รายการ` : undefined}
                                    >
                                        คำร้อง
                                        {requestsBadge > 0 && (
                                            <span className="nav-badge nav-badge-work">{formatBadge(requestsBadge)}</span>
                                        )}
                                    </Link>
                                )}
                                <Link
                                    to="/admin"
                                    className={`nav-link nav-link-work ${isActive('/admin') && !isActive('/admin/chat') && !isActive('/admin/requests') ? 'active' : ''}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    title={adminBadge > 0 ? `งานรอดำเนินการ ${adminBadge} รายการ` : undefined}
                                >
                                    จัดการระบบ
                                    {adminBadge > 0 && (
                                        <span className="nav-badge nav-badge-work">{formatBadge(adminBadge)}</span>
                                    )}
                                </Link>
                                {!isAdmin() && hasPermission('chat.admin') && (
                                    <Link
                                        to="/admin/chat"
                                        className={`nav-link nav-link-chat ${isActive('/admin/chat') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        แชท
                                        {adminUnreadCount > 0 && (
                                            <span className="nav-badge">{formatBadge(adminUnreadCount)}</span>
                                        )}
                                    </Link>
                                )}
                            </>
                        )}
                        {/* ช่วยจำยอดสหกรณ์ออมทรัพย์ — ข้อมูลส่วนตัวของทุกคน (รวมเจ้าหน้าที่/admin ที่เป็นสมาชิกสหกรณ์) */}
                        <Link
                            to="/coop"
                            className={`nav-link nav-link-coop ${isActive('/coop') ? 'active' : ''}`}
                            title="ช่วยจำยอดเงินสหกรณ์ออมทรัพย์ รพ.ศก."
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" />
                                <path d="M16 12h5" />
                                <circle cx="16" cy="12" r="1.5" />
                            </svg>
                            สหกรณ์
                        </Link>
                        {/* ประชาสัมพันธ์ระบบแจ้งซ่อมคอมพิวเตอร์ (ลิงก์ภายนอก แสดงทุกคน) */}
                        <a
                            href="https://iservice.sisakethospital.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="nav-link nav-link-iservice"
                            title="แจ้งซ่อมคอมพิวเตอร์ / IT (iService)"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                            </svg>
                            แจ้งซ่อมคอม
                        </a>
                    </nav>

                    <div className="header-right">
                        {onlineCount > 0 && (
                            <div
                                className="online-indicator"
                                title={`กำลังใช้งานระบบอยู่ ${onlineCount} คน`}
                            >
                                <span className="online-dot" aria-hidden="true"></span>
                                <svg
                                    className="online-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {/* key เปลี่ยนตามจำนวน → React สร้าง span ใหม่ ทำให้ animation เด้งเล่นทุกครั้งที่ตัวเลขเปลี่ยน */}
                                <span key={onlineCount} className="online-count bump">{onlineCount}</span>
                                <span className="online-label hide-mobile">ออนไลน์</span>
                            </div>
                        )}

                        <div className="user-menu">
                            <Link to="/profile" className="user-info">
                                <div className={`user-avatar ${user?.avatar_url ? 'has-image' : ''}`}>
                                    {user?.avatar_url ? (
                                        <img src={`/api${user.avatar_url}`} alt={user?.fullname || 'avatar'} />
                                    ) : (
                                        user?.fullname?.charAt(0)?.toUpperCase() || '$'
                                    )}
                                </div>
                                <span className="user-name hide-mobile">{user?.fullname || 'User'}</span>
                            </Link>
                            <button onClick={handleLogout} className="logout-btn" title="ออกจากระบบ">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                    <polyline points="16,17 21,12 16,7"/>
                                    <line x1="21" y1="12" x2="9" y2="12"/>
                                </svg>
                                <span className="logout-text hide-mobile">ออกจากระบบ</span>
                            </button>
                        </div>

                        <button
                            className="mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {mobileMenuOpen ? (
                                    <path d="M18 6L6 18M6 6l12 12"/>
                                ) : (
                                    <path d="M3 12h18M3 6h18M3 18h18"/>
                                )}
                            </svg>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="main-content">
                <div className="container">
                    <EmailVerifyBanner />
                    {/* แถบบอกทางงานรอดำเนินการ (เจ้าหน้าที่) — กดจาก LINE เข้ามาแล้วรู้ทันทีว่าต้องไปหน้าไหน */}
                    <PendingWorkBanner />
                    <Outlet />
                </div>
            </main>

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <p>{appName} - ระบบดูข้อมูลเงินเดือน</p>
                    <p className="text-muted">โรงพยาบาลศรีสะเกษ</p>
                    <p className="text-muted footer-credit">{appCredit}</p>
                </div>
            </footer>

            {/* Chat Bubble - ปิดชั่วคราว ระบบแชทใช้ไม่ได้ (ปลดปิดได้โดยลบ false &&) */}
            {false && !canAccessAdmin() && <ChatBubble />}

            {/* ปุ่มติดต่อเจ้าหน้าที่ — เฉพาะผู้ใช้ทั่วไป (ไม่ใช่เจ้าหน้าที่) */}
            {!canAccessAdmin() && <ContactWidget />}

            {/* Popup ชวนติดตั้งลงหน้าจอหลัก — เด้งเฉพาะหลังล็อกอิน (อยู่ใน Layout) */}
            <InstallPWAButton />

            {/* Popup เชิญบันทึกยอดสหกรณ์ออมทรัพย์ — เด้งจนกว่าจะบันทึกข้อมูล หรือกด "ไม่ต้องแสดงอีก" */}
            <CoopInvitePopup />

            {/* Popup ชวนรีวิวดาว — เด้งทุกต้นเดือนจนกว่าจะรีวิว (รีวิวได้ในตัว popup เลย) */}
            <ReviewInvitePopup />
        </div>
    );
}

export default Layout;
